import React, { useState, useEffect, useCallback } from 'react';
import { useToast } from '../contexts/ToastContext';
import { AddCategoryModal } from './modal/AddCategoryModal';
import { 
    ToastPreferences,
    getToastPreferences,
    saveToastPreferences,
    addToastCategory,
    removeToastCategory
} from '../utils/ToastPreferences';

function AddCategoryButton({ onAddCategory }: { onAddCategory: (category: string, options: { persist: boolean; duration: number }) => void }) {
    const [showModal, setShowModal] = useState(false);

    return (
        <>
            <button
                onClick={() => setShowModal(true)}
                className="btn btn-primary"
            >
                Add Category
            </button>
            <AddCategoryModal
                show={showModal}
                onClose={() => setShowModal(false)}
                onSubmit={onAddCategory}
            />
        </>
    );
}

export function ToastSettings() {
    const { position, updatePosition } = useToast();
    const [preferences, setPreferences] = useState(getToastPreferences());

    // Refresh preferences when they change
    useEffect(() => {
        const handleStorageChange = () => {
            setPreferences(getToastPreferences());
        };

        window.addEventListener('storage', handleStorageChange);
        return () => window.removeEventListener('storage', handleStorageChange);
    }, []);

    // Refresh preferences after local changes
    const refreshPreferences = useCallback(() => {
        setPreferences(getToastPreferences());
    }, []);

    const handleDurationChange = useCallback((duration: number) => {
        saveToastPreferences({ duration });
        refreshPreferences();
    }, [refreshPreferences]);

    const handleMaxToastsChange = useCallback((maxToasts: number) => {
        saveToastPreferences({ maxToasts });
        refreshPreferences();
    }, [refreshPreferences]);

    const handleCategoryChange = useCallback((
        category: string,
        options: { persist: boolean; duration: number }
    ) => {
        addToastCategory(category, options);
        refreshPreferences();
    }, [refreshPreferences]);

    const handleRemoveCategory = useCallback((category: string) => {
        removeToastCategory(category);
        refreshPreferences();
    }, [refreshPreferences]);

    return (
        <div className="space-y-6">
            <div>
                <h3 className="text-lg font-medium">Toast Notifications</h3>
                <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                    Customize how notifications appear and behave.
                </p>
            </div>

            <div className="space-y-4">
                <div>
                    <label className="block text-sm font-medium mb-2">Position</label>
                    <div className="grid grid-cols-2 gap-3">
                        {(['top-right', 'top-left', 'bottom-right', 'bottom-left'] as const).map(pos => (
                            <button
                                key={pos}
                                onClick={() => updatePosition(pos)}
                                className={`px-4 py-2 rounded-lg border ${
                                    position === pos
                                        ? 'border-primary bg-primary/10 text-primary'
                                        : 'border-gray-200 dark:border-gray-700 hover:border-primary/50'
                                }`}
                            >
                                {pos.split('-').map(word => 
                                    word.charAt(0).toUpperCase() + word.slice(1)
                                ).join(' ')}
                            </button>
                        ))}
                    </div>
                </div>

                <div>
                    <label className="block text-sm font-medium mb-2">Default Duration (seconds)</label>
                    <input
                        type="number"
                        min="1"
                        max="30"
                        value={preferences.duration / 1000}
                        onChange={(e) => handleDurationChange(Number(e.target.value) * 1000)}
                        className="input w-full"
                    />
                </div>

                <div>
                    <label className="block text-sm font-medium mb-2">Maximum Visible Toasts</label>
                    <input
                        type="number"
                        min="1"
                        max="10"
                        value={preferences.maxToasts}
                        onChange={(e) => handleMaxToastsChange(Number(e.target.value))}
                        className="input w-full"
                    />
                </div>

                <div>
                    <label className="block text-sm font-medium mb-2">Categories</label>
                    <div className="space-y-3">
                        {Object.entries(preferences.categories).map(([category, options]) => (
                            <div key={category} className="flex items-center gap-4 p-3 rounded-lg border border-gray-200 dark:border-gray-700">
                                <div className="flex-1">
                                    <h4 className="font-medium">{category}</h4>
                                    <p className="text-sm text-gray-500 dark:text-gray-400">
                                        {options.persist ? 'Persistent' : `${options.duration / 1000}s duration`}
                                    </p>
                                </div>
                                <div className="flex items-center gap-2">
                                    <button
                                        onClick={() => handleCategoryChange(category, {
                                            ...options,
                                            persist: !options.persist,
                                        })}
                                        className="btn btn-ghost"
                                    >
                                        {options.persist ? 'Make Temporary' : 'Make Persistent'}
                                    </button>
                                    {category !== 'system' && (
                                        <button
                                            onClick={() => handleRemoveCategory(category)}
                                            className="btn btn-ghost text-error"
                                        >
                                            Remove
                                        </button>
                                    )}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </div>

            <div className="pt-4 border-t border-gray-200 dark:border-gray-700">
                <AddCategoryButton onAddCategory={handleCategoryChange} />
            </div>
        </div>
    );
}
